import { useRef, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import ChatInput from '../chat/ChatInput'
import ChatMessage from '../chat/ChatMessage'
import { useChatStore } from '../../stores/chatStore'
import { getFeaturedCapabilities, type FeaturedCapabilitiesResponse } from '../../lib/api'

// Hand-curated capabilities for the homepage — sourced from probed DB data
interface CuratedCapability {
  name: string           // capability name used for search
  displayName: string    // human-friendly name
  description: string    // what it does
  type: 'mcp' | 'a2a'   // protocol
  agentName: string      // which agent provides it
  agentKey: string       // agent_key for direct linking
  agentImage?: string    // agent profile image URL
  free: boolean | null   // true=free, false=paid, null=unknown
}

const FEATURED_CAPABILITIES: CuratedCapability[] = [
  {
    name: 'audit_x_account',
    displayName: 'Check if a Twitter account is a bot',
    description: 'Analyze account authenticity and detect automated behavior',
    type: 'mcp',
    agentName: 'Double O Seven',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:13427',
    agentImage: 'https://moccasin-managing-reptile-137.mypinata.cloud/ipfs/bafkreiaczkk5w3dbvnp6m5ae4hrfsfk67wea22tljzcjbh4y2k6kkd2xei',
    free: true,
  },
  {
    name: 'fetch_pdf',
    displayName: 'Extract text from a PDF',
    description: 'Pull structured text content with page numbers from any PDF document',
    type: 'mcp',
    agentName: 'XPRTL MCP',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:13025',
    agentImage: 'https://gateway.pinata.cloud/ipfs/QmadarrA34crWZRQwhjC1PgnAQp6LDJQZqsRpFC1E9FYTY',
    free: false,
  },
  {
    name: 'astrology_chat',
    displayName: 'Get an astrology reading',
    description: 'Chat with Astro Merkat for personalized astrological insights',
    type: 'mcp',
    agentName: 'Astro Merkat',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:22693',
    agentImage: 'https://www.meerkat.town/meerkats/meerkat_094.png',
    free: true,
  },
  {
    name: 'trip_planning_chat',
    displayName: 'Plan a trip',
    description: 'Get help planning your next adventure with personalized recommendations',
    type: 'mcp',
    agentName: 'Hailey',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:20019',
    agentImage: 'https://www.meerkat.town/meerkats/meerkat_068.png',
    free: true,
  },
  {
    name: 'coinbase_price',
    displayName: 'Get ETH price',
    description: 'Real-time cryptocurrency prices and exchange rates',
    type: 'mcp',
    agentName: 'XPRTL MCP',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:13025',
    agentImage: 'https://gateway.pinata.cloud/ipfs/QmadarrA34crWZRQwhjC1PgnAQp6LDJQZqsRpFC1E9FYTY',
    free: true,
  },
  {
    name: 'swap',
    displayName: 'Swap USDC to ETH',
    description: 'Execute token swaps on Base using DEX aggregator',
    type: 'mcp',
    agentName: 'XPRTL MCP',
    agentKey: '1:0x8004a169fb4a3325136eb29fa0ceb6d2e539a432:13025',
    agentImage: 'https://gateway.pinata.cloud/ipfs/QmadarrA34crWZRQwhjC1PgnAQp6LDJQZqsRpFC1E9FYTY',
    free: false,
  },
]

interface MainContentProps {
  hasConversation: boolean
}

export default function MainContent({ hasConversation }: MainContentProps) {
  const { messages, isLoading } = useChatStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Scroll to top when new messages arrive (so user sees search results from the beginning)
  useEffect(() => {
    const container = messagesEndRef.current?.parentElement
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [messages.length])

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {hasConversation ? (
        // Conversation view
        <>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
              <AnimatePresence mode="popLayout">
                {messages.map((message, index) => {
                  const isLatestAssistant = message.role === 'assistant' && index === messages.length - 1
                  return (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                    >
                      <ChatMessage message={message} isLatest={isLatestAssistant} />
                    </motion.div>
                  )
                })}
              </AnimatePresence>

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 text-[var(--text-muted)] font-mono text-sm"
                >
                  <LoadingSpinner />
                  <span>Searching agents...</span>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input at bottom */}
          <div className="border-t border-[var(--border-subtle)] p-4">
            <div className="max-w-3xl mx-auto">
              <ChatInput compact />
            </div>
          </div>
        </>
      ) : (
        // Welcome view - centered
        <WelcomeView />
      )}
    </div>
  )
}

function WelcomeView() {
  const [featured, setFeatured] = useState<FeaturedCapabilitiesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    getFeaturedCapabilities()
      .then(setFeatured)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="inline-flex items-center gap-3 mb-4"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="w-14 h-14 rounded-xl bg-[var(--accent-teal)] flex items-center justify-center shadow-lg shadow-[var(--accent-teal)]/20">
              <span className="font-mono font-bold text-xl text-[var(--bg-primary)]">8K</span>
            </div>
          </motion.div>
          <h1 className="font-display text-4xl font-bold mb-3">
            Connect with Verified AI Agents
          </h1>
          <p className="text-[var(--text-muted)] font-mono text-sm mb-8 max-w-xl mx-auto">
            Discover, Interact and Review <a href="https://eips.ethereum.org/EIPS/eip-8004" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-teal)] hover:underline">ERC-8004</a> agents
          </p>

          {/* Search Input */}
          <div className="max-w-2xl mx-auto">
            <ChatInput autoFocus />
          </div>
        </motion.div>

        {/* Stats Banner */}
        {featured && (
          <motion.div
            className="flex justify-center gap-8 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-[var(--text-primary)]">
                {featured.stats.agents_with_working_capabilities}
              </div>
              <div className="text-xs font-mono text-[var(--text-muted)] uppercase">Verified Agents</div>
            </div>
            <div className="w-px bg-[var(--border-subtle)]" />
            <div className="text-center">
              <div className="text-2xl font-bold text-[var(--accent-teal)]">
                {featured.stats.free_capabilities}
              </div>
              <div className="text-xs font-mono text-[var(--text-muted)] uppercase">Free Tools</div>
            </div>
            <div className="w-px bg-[var(--border-subtle)]" />
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400">
                {featured.stats.paid_capabilities}
              </div>
              <div className="text-xs font-mono text-[var(--text-muted)] uppercase">Paid Tools</div>
            </div>
            <div className="w-px bg-[var(--border-subtle)]" />
            <div className="text-center">
              <div className="text-2xl font-bold text-[var(--text-primary)]">
                {featured.stats.total_registered?.toLocaleString()}
              </div>
              <div className="text-xs font-mono text-[var(--text-muted)] uppercase">Registered</div>
            </div>
          </motion.div>
        )}

        {/* Popular Capabilities Showcase */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold mb-2">Popular Tasks</h2>
            <p className="text-base text-[var(--text-muted)]">
              Real tasks agents can accomplish for you right now
            </p>
          </div>

          {/* 2 tasks per line */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {FEATURED_CAPABILITIES.map((cap, i) => (
              <CuratedCapabilityCard key={cap.name} capability={cap} index={i} />
            ))}
          </div>
        </motion.section>

        {/* Loading State for stats */}
        {loading && !featured && (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  )
}

function CuratedCapabilityCard({
  capability,
  index,
}: {
  capability: CuratedCapability
  index: number
}) {
  const navigate = useNavigate()

  const handleClick = () => {
    const param = capability.type === 'mcp' ? 'tool' : 'skill'
    navigate(`/agents/${capability.agentKey}?${param}=${encodeURIComponent(capability.name)}`)
  }

  return (
    <motion.button
      onClick={handleClick}
      className="group relative h-full p-6 rounded-2xl bg-white dark:bg-[var(--bg-secondary)] border border-gray-200 dark:border-[var(--border-default)] hover:shadow-xl hover:scale-[1.02] transition-all duration-300 text-left overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.08, duration: 0.5 }}
    >
      {/* Subtle hover gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative flex flex-col h-full gap-5">
        {/* Task headline - most prominent */}
        <div className="space-y-2">
          <h3 className="font-display text-2xl font-semibold text-gray-900 dark:text-[var(--text-primary)] leading-snug">
            {capability.displayName}
          </h3>
          <p className="text-sm text-gray-600 dark:text-[var(--text-muted)] leading-relaxed">
            {capability.description}
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-[var(--border-default)] to-transparent" />

        {/* Agent info footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {capability.agentImage ? (
              <img
                src={capability.agentImage}
                alt={capability.agentName}
                className="w-10 h-10 rounded-lg object-cover bg-gray-100 dark:bg-[var(--bg-tertiary)]"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-100 to-purple-100 dark:from-teal-900/30 dark:to-purple-900/30 flex items-center justify-center">
                <span className="text-lg font-semibold text-teal-700 dark:text-teal-400">
                  {(capability.agentName || '?')[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-500 dark:text-[var(--text-muted)]">by</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-[var(--text-primary)]">
                {capability.agentName}
              </div>
            </div>
          </div>

          {/* Price badge */}
          {capability.free === true && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              Free
            </span>
          )}
          {capability.free === false && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              Paid
            </span>
          )}
        </div>
      </div>

      {/* Hover indicator */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-teal-400/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.button>
  )
}

function LoadingSpinner() {
  return (
    <motion.div
      className="w-5 h-5 border-2 border-[var(--accent-teal)] border-t-transparent rounded-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    />
  )
}
